import { useSyncExternalStore } from "react";
import { Capacitor } from "@capacitor/core";
import { BackgroundGeolocation } from "@capgo/background-geolocation";
import { Geolocation } from "@capacitor/geolocation";

// Single-engine mileage tracker. On a native build with the background plugin
// it runs ONE foreground-service engine that keeps tracking with the app
// closed (e.g. in Google Maps) and shows the persistent "tap to stop"
// notification. On web / builds without the plugin it falls back to a simple
// foreground poll. No second engine, no per-second timers — the churn that
// froze earlier versions is gone. State lives at module scope so the trip and
// its accumulated miles survive navigating between pages.

type Phase = "idle" | "tracking" | "review";
export type TripState = {
  phase: Phase;
  miles: number;
  startedAt: number;
  autoStopped: boolean;
  paused: boolean;
  error: string | null;
};

const STORAGE_KEY = "mileageTripV2";
const ACCURACY_MAX_M = 100;
const MIN_SEG_MI = 0.005; // ~8m: drop jitter while stopped
const MAX_SEG_MI = 8; // drop teleport glitches
const AWAY_MI = 0.3;
const RETURN_MI = 0.1;
const FIX_WATCHDOG_MS = 30000; // if the bg engine delivers no fix this long, fall back to foreground

let state: TripState = { phase: "idle", miles: 0, startedAt: 0, autoStopped: false, paused: false, error: null };
const listeners = new Set<() => void>();

let origin: { lat: number; lng: number } | null = null;
let lastFix: { lat: number; lng: number } | null = null;
let maxDist = 0;
let running = false;
let wakeLock: any = null;
let usingBg = false;
let pollTimer: number | null = null;
let polling = false;
let hadFix = false;
let fixWatchdog: number | null = null;

function emit() {
  for (const l of listeners) l();
}

function persist() {
  try {
    if (state.phase !== "idle") {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ phase: state.phase, miles: state.miles, startedAt: state.startedAt, autoStopped: state.autoStopped }),
      );
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

function set(patch: Partial<TripState>) {
  state = { ...state, ...patch };
  persist();
  emit();
}

function haversineMiles(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 3958.7613;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const lat1 = (aLat * Math.PI) / 180;
  const lat2 = (bLat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function handleFix(lat: number, lng: number, accuracy?: number) {
  if (!running) return;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
  // Any delivered coordinate means the engine is alive — cancel the fallback.
  hadFix = true;
  if (fixWatchdog) {
    window.clearTimeout(fixWatchdog);
    fixWatchdog = null;
  }
  if (typeof accuracy === "number" && accuracy > 0 && accuracy > ACCURACY_MAX_M) return;
  if (state.error) set({ error: null });
  const cur = { lat, lng };
  if (!origin) origin = cur;
  if (lastFix) {
    const seg = haversineMiles(lastFix.lat, lastFix.lng, cur.lat, cur.lng);
    if (seg >= MIN_SEG_MI && seg <= MAX_SEG_MI) set({ miles: state.miles + seg });
  }
  lastFix = cur;
  const distOrigin = haversineMiles(origin.lat, origin.lng, cur.lat, cur.lng);
  if (distOrigin > maxDist) maxDist = distOrigin;
  if (maxDist > AWAY_MI && distOrigin < RETURN_MI && state.miles > 0) {
    void stopTrip(true);
  }
}

export function canBackgroundTrack(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("BackgroundGeolocation");
}

async function startEngine() {
  try {
    wakeLock = await (navigator as any).wakeLock?.request("screen");
  } catch {
    /* best effort */
  }
  usingBg = canBackgroundTrack();
  if (usingBg) {
    await BackgroundGeolocation.start(
      {
        backgroundTitle: "Stoop is tracking your trip",
        backgroundMessage: "Logging mileage — tap to return, then End trip when you're done.",
        requestPermissions: true,
        stale: false,
        distanceFilter: 20,
      },
      (location, error) => {
        if (error || !location) {
          if (error && running) set({ error: "Waiting for GPS signal…" });
          return;
        }
        handleFix(location.latitude, location.longitude, location.accuracy);
      },
    );
    // If @capgo stays silent (no fix at all) past the watchdog, the
    // background service isn't delivering in this build — stop it and fall back
    // to the proven foreground getCurrentPosition poll so the user isn't stuck
    // on "Waiting for GPS signal…" forever. Only ONE engine runs at a time, so
    // this never re-creates the two-engine bridge flood that ANR'd earlier.
    fixWatchdog = window.setTimeout(() => void fallbackToForeground(), FIX_WATCHDOG_MS);
    return;
  }
  await startForegroundPoll();
}

async function fallbackToForeground() {
  if (!running || hadFix || pollTimer) return;
  if (usingBg) {
    usingBg = false;
    try {
      await BackgroundGeolocation.stop();
    } catch {
      /* ignore */
    }
  }
  await startForegroundPoll();
}

// Foreground engine (web / no plugin / bg-engine fell silent): poll
// getCurrentPosition. This mirrors DriverGPSSender, the proven-reliable path.
async function startForegroundPoll() {
  if (pollTimer) return;
  if (Capacitor.isNativePlatform()) {
    try {
      const perm = await Geolocation.requestPermissions();
      if (perm.location !== "granted" && perm.coarseLocation !== "granted") {
        set({ error: "Location permission is needed to track mileage." });
        return;
      }
    } catch {
      /* ignore */
    }
  }
  const tick = async () => {
    if (!running || polling) return;
    polling = true;
    try {
      const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 30000, maximumAge: 10000 });
      handleFix(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy);
    } catch {
      if (running) set({ error: "Searching for GPS — keep this screen open with a clear view of the sky." });
    } finally {
      polling = false;
    }
  };
  void tick();
  pollTimer = window.setInterval(() => void tick(), 10000);
}

async function stopEngine() {
  running = false;
  if (fixWatchdog) {
    window.clearTimeout(fixWatchdog);
    fixWatchdog = null;
  }
  if (pollTimer) {
    window.clearInterval(pollTimer);
    pollTimer = null;
  }
  if (usingBg) {
    try {
      await BackgroundGeolocation.stop();
    } catch {
      /* ignore */
    }
  }
  try {
    await wakeLock?.release?.();
  } catch {
    /* ignore */
  }
  wakeLock = null;
}

export async function startTrip() {
  origin = null;
  lastFix = null;
  maxDist = 0;
  hadFix = false;
  running = true;
  set({ phase: "tracking", miles: 0, startedAt: Date.now(), autoStopped: false, paused: false, error: null });
  await startEngine();
}

export async function pauseTrip() {
  if (state.phase !== "tracking" || state.paused) return;
  await stopEngine();
  set({ paused: true, error: null });
}

export async function resumeTrip() {
  if (state.phase !== "tracking" || !state.paused) return;
  lastFix = null; // don't count the distance skipped while paused
  running = true;
  set({ paused: false });
  await startEngine();
}

export async function stopTrip(auto = false) {
  await stopEngine();
  set({ phase: "review", autoStopped: auto, paused: false });
}

export function startManual() {
  void stopEngine();
  origin = null;
  lastFix = null;
  maxDist = 0;
  set({ phase: "review", miles: 0, startedAt: 0, autoStopped: false, paused: false, error: null });
}

export async function discardTrip() {
  await stopEngine();
  origin = null;
  lastFix = null;
  maxDist = 0;
  set({ phase: "idle", miles: 0, startedAt: 0, autoStopped: false, paused: false, error: null });
}

export function setMiles(miles: number) {
  set({ miles });
}

// On load, surface any persisted trip in the review step (don't silently
// restart GPS) so it's logged or discarded — never stuck.
try {
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw) {
    const s = JSON.parse(raw);
    if (s && (s.phase === "tracking" || s.phase === "review")) {
      state = { phase: "review", miles: Number(s.miles) || 0, startedAt: Number(s.startedAt) || 0, autoStopped: !!s.autoStopped, paused: false, error: null };
    }
  }
} catch {
  /* ignore */
}

export function useTrip(): TripState {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
    () => state,
  );
}
